// ABOUTME: Drag-and-drop file selector for images with optional preview from URL or local file.
// ABOUTME: Supports previewUrl prop for existing images, variant prop for logo vs sideImage styling.

import React, { useCallback, useRef, useState } from 'react';
import { ReactComponent as Border } from '../../../../styles/svg/border.svg';
import { Box, ButtonBase, useTheme } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Trans, useTranslation } from 'react-i18next';
import { tokens } from '../../../../theme';
import Subheading from '../../text/Subheading';
import Text from '../../text/Text';
import SHModal from '../../Modal/SHModal';

const UploadLogoSelector = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {t} = useTranslation();

    const [selectedFile, setSelectedFile] = useState(null);
    const [localPreview, setLocalPreview] = useState(props.previewUrl || null);
    const [fullPreviewOpen, setFullPreviewOpen] = useState(false);
    const fileInputRef = useRef(null);

    const previewSrc = localPreview;

    const handleClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDrag = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            selectFile(event.dataTransfer.files[0]);
        }
    }, []);

    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            selectFile(event.target.files[0]);
        }
        // Reset so the same file can be re-selected
        event.target.value = '';
    };

    const selectFile = (file) => {
        setSelectedFile(file);
        setLocalPreview(URL.createObjectURL(file));
        if (props.onFileSelect) {
            props.onFileSelect(file);
        }
    };

    const removeFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        setLocalPreview(null);
        if (props.onFileSelect) {
            props.onFileSelect(null);
        }
        if (props.onRemove) {
            props.onRemove();
        }
    };

    const breakpoint = props.breakpoint ?? "phone";
    const getBreakPoint = () => {
        if(breakpoint == "phone") return 768;
        if(breakpoint == "tablet") return 1024;
        if(breakpoint == "smallpc") return 1366;
    }

    // variant: "logo" = centered logo on tinted bg, "sideImage" = cover image with dark overlay
    const variant = props.variant || "logo";

    return (
        <>
        {/* Full preview modal */}
        {previewSrc && (
            <SHModal open={fullPreviewOpen} onClose={() => setFullPreviewOpen(false)}>
                <Box sx={{
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& img": {
                        maxWidth: "80vw",
                        maxHeight: "80vh",
                        objectFit: "contain",
                        borderRadius: 3,
                    }
                }}>
                    <img src={previewSrc} alt={props.title} />
                </Box>
            </SHModal>
        )}
        <Box sx={{ flex: 1, flexShrink: 0, display: "flex", flexDirection: "column" }}>
            <ButtonBase
                component="div"
                onDragEnd={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
                TouchRippleProps={{
                    style: {
                        color: colors[props.color || "primary"][200],
                    }
                }}
                sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    flex: 1,
                    background: previewSrc
                        ? (variant === "sideImage"
                            ? "transparent"
                            : (props.brandColor
                                ? `rgba(${props.brandColor.join(',')}, 0.08)`
                                : (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100])))
                        : colors[props.color || "primary"][100],
                    display: "flex",
                    width: 1,
                    "& .border": {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: 1,
                        width: 1,
                        borderRadius: 3,
                        "& path": {
                            fill: "none",
                            stroke: colors[props.color || "primary"][400],
                            strokeWidth: 4,
                            strokeDasharray: "10,10",
                            animation: "dash 25s linear infinite",
                            animationPlayState: "paused"
                        },
                    },
                    "&:hover path": {
                        animationPlayState: "running"
                    },
                }}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept="image/jpeg, image/png, image/jpg"
                />

                {!previewSrc && <Border className='border' />}

                {/* Preview image as background of the container */}
                {previewSrc && (
                    <>
                        {variant === "sideImage" ? (
                            <img src={previewSrc} alt={props.title} style={{
                                position: "absolute", top: 0, left: 0,
                                width: "100%", height: "100%",
                                objectFit: "cover", zIndex: 0,
                            }} />
                        ) : (
                            <img src={previewSrc} alt={props.title} style={{
                                position: "absolute", top: "50%", left: "50%",
                                transform: "translate(-50%, -50%)",
                                maxHeight: 48, maxWidth: "80%",
                                objectFit: "contain", zIndex: 0,
                            }} />
                        )}
                        <Box sx={{
                            position: "absolute", top: 0, left: 0,
                            width: 1, height: 1,
                            background: "rgba(0,0,0,0.45)", zIndex: 1,
                        }} />
                        {/* Action buttons */}
                        <Box sx={{
                            position: "absolute", top: 8, right: 8,
                            display: "flex", gap: 0.5, zIndex: 3,
                        }}>
                            <ButtonBase
                                onClick={(e) => { e.stopPropagation(); setFullPreviewOpen(true); }}
                                sx={{
                                    height: 28, width: 28, borderRadius: 3,
                                    background: "rgba(255,255,255,0.9)",
                                    color: colors.txt["primary"],
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                            >
                                <OpenInFullIcon sx={{ fontSize: 14 }} />
                            </ButtonBase>
                            <ButtonBase
                                onClick={(e) => { e.stopPropagation(); removeFile(e); }}
                                sx={{
                                    height: 28, width: 28, borderRadius: 3,
                                    background: "rgba(255,255,255,0.9)",
                                    color: colors.red[400],
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                            >
                                <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                            </ButtonBase>
                        </Box>
                    </>
                )}

                <Box sx={{ position: "relative", top: 0, left: 0, width: 1, display: "flex", zIndex: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "column",
                            paddingX: 3,
                            paddingTop: 4,
                            paddingBottom: selectedFile ? 4 : 6,
                            width: 1,
                            [`@media screen and (max-width: ${getBreakPoint()}px)`]: {
                                paddingBottom: 4,
                                alignItems: "flex-start",
                            }
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "column",
                                [`@media screen and (max-width: ${getBreakPoint()}px)`]: {
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                    gap: 2,
                                    alignItems: "flex-start"
                                }
                            }}
                        >
                            <Box sx={{ marginBottom: 2, [`@media screen and (max-width: ${getBreakPoint()}px)`]: { marginBottom: 0 } }}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 5,
                                        background: previewSrc ? "rgba(255,255,255,0.15)" : colors[props.color || "primary"][100],
                                        color: previewSrc
                                            ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"])
                                            : colors[props.color || "primary"][400],
                                        "& svg path": {
                                            fill: previewSrc
                                                ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"])
                                                : colors[props.color || "primary"][400]
                                        }
                                    }}
                                >
                                    {props.icon}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexDirection: "column",
                                    textAlign: "center",
                                    gap: 0.5,
                                    color: previewSrc
                                        ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"])
                                        : undefined,
                                    ".colored": {
                                        color: previewSrc
                                            ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"])
                                            : colors[props.color || "primary"][400]
                                    },
                                    [`@media screen and (max-width: ${getBreakPoint()}px)`]: {
                                        textAlign: "left",
                                        alignItems: "flex-start"
                                    }
                                }}
                            >
                                <Subheading color={previewSrc ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"]) : undefined}>{props.title}</Subheading>
                                <Box>
                                    <Trans
                                        i18nKey="uploadFileText"
                                        values={{ clickHere: t('clickHereText') }}
                                        components={{
                                            strong: <strong className="colored" />
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {selectedFile &&
                            <Box
                                sx={{
                                    paddingLeft: 2,
                                    paddingRight: 0.5,
                                    height: 40,
                                    borderRadius: 5,
                                    background: previewSrc ? "rgba(255,255,255,0.15)" : colors[props.color][100],
                                    marginTop: 2,
                                    color: previewSrc
                                        ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"])
                                        : colors[props.color][400],
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    [`@media screen and (max-width: ${getBreakPoint()}px)`]: {
                                        width: "calc(100% - 56px)",
                                        marginLeft: 7,
                                    }
                                }}
                            >
                                <Text
                                    noBreak
                                    color={previewSrc ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"]) : undefined}
                                    variant={previewSrc ? undefined : (props.color || "primary")}
                                >
                                    {selectedFile.name}
                                </Text>

                                <ButtonBase
                                    onClick={removeFile}
                                    sx={{
                                        marginLeft: 2,
                                        height: 32,
                                        paddingX: 1.5,
                                        background: previewSrc ? "rgba(255,255,255,0.25)" : colors[props.color][400],
                                        borderRadius: 4,
                                        color: previewSrc
                                            ? (theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"])
                                            : (theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light']),
                                        fontWeight: 700,
                                    }}
                                >
                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                </ButtonBase>
                            </Box>
                        }
                    </Box>
                </Box>
            </ButtonBase>
        </Box>
        </>
    );
}

export default UploadLogoSelector;
